// MentionEditor.tsx
import { useCallback, useEffect, useRef } from "react";
import { EditorContent, useEditor, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";

type IUserItem = {
  id: string;
  label: string;
  avatar?: string | null;
};

type Props = {
  users: IUserItem[];
  initialHtml?: string;
  onChange?: (
    mentionIds: string[],
    commentHtml: string,
    mentionsMeta?: { id: string; label: string }[]
  ) => void;
  placeholder?: string;
  getEditor?: (editor: Editor | null) => void;
};

export default function MentionEditor({
  users,
  initialHtml = "",
  onChange,
  placeholder = "Type your comment. Use @ to mention someone...",
  getEditor,
}: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Mention.configure({
        HTMLAttributes: {
          class:
            "mention-chip inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-semibold bg-blue-100 border border-blue-300 text-blue-800",
        },
        suggestion: {
          char: "@",
          startOfLine: false,
          allow: () => true,
          // ⭐ CRITICAL FIX: The command function now correctly replaces the entire suggestion range
          command: ({ editor: ed, range, props: item }: any) => {
            // Get the current text of the query. This is the text between '@' and the cursor.

            // const queryText = ed.view.state.doc.textBetween(
            //   range.from,
            //   range.to + searchQuery.current.length
            // );
            // console.log(range);
            // console.log("queryText: ", queryText);
            // Calculate the start position for deletion:
            // This needs to cover the '@' symbol (range.from - 1) and the entire query.
            const deletionFrom = range.from - 1;
            const deletionTo = range.to + searchQuery.current.length;

            // 1. Start the transaction chain.
            ed.chain()
              .focus()
              // 2. Explicitly delete the range covering the '@' and the query text.
              // If we delete the widest possible range first, the leftover character must go.
              .deleteRange({ from: deletionFrom, to: deletionTo })
              // 3. Insert the mention and a space at the new cursor position (which is deletionFrom).
              // We use insertContentAt(deletionFrom, ...) to ensure it lands where the '@' was.
              .insertContentAt(deletionFrom, [
                { type: "mention", attrs: { id: item.id, label: item.label } },
                { type: "text", text: " " },
              ])
              .run();
          },
          items: ({ query }: { query: string }) => {
            const q = (query || "").toLowerCase().trim();
            searchQuery.current = q;
            if (!q) return users.slice(0, 8);
            return users
              .filter((u) => u.label.toLowerCase().includes(q))
              .slice(0, 8);
          },

          render: () => {
            let component: HTMLDivElement | null = null;
            let currentState: {
              items: any[];
              selectedIndex: number;
              query: string;
              setSelectedIndex: (index: number) => void;
              command: (item: any) => void;
              exit: () => void;
            } | null = null;

            const updateComponent = () => {
              if (!component || !currentState) return;

              const { items, selectedIndex } = currentState;
              component.innerHTML = "";

              if (!items || items.length === 0) {
                const emptyState = document.createElement("div");
                emptyState.className = "px-3 py-2 text-gray-500 text-sm";
                emptyState.textContent = "No users found";
                component.appendChild(emptyState);
                return;
              }

              const list = document.createElement("div");
              list.className = "divide-y divide-gray-100";

              items.forEach((item: any, index: number) => {
                const itemElement = document.createElement("div");
                itemElement.setAttribute("role", "option");
                itemElement.setAttribute("data-index", String(index));

                let className =
                  "px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors text-sm";
                if (index === selectedIndex) {
                  className += " bg-gray-100 text-blue-600";
                }
                itemElement.className = className;

                itemElement.innerHTML = `<div class="truncate">${item.label}</div>`;

                itemElement.addEventListener("mouseenter", () => {
                  if (currentState?.setSelectedIndex) {
                    currentState.setSelectedIndex(index);
                    // Force the local UI update on hover as well
                    if (currentState) {
                      currentState.selectedIndex = index;
                      updateComponent();
                    }
                  }
                });

                itemElement.addEventListener("mousedown", (e) => {
                  // console.log("Clicked");
                  e.preventDefault();
                  e.stopPropagation();
                  if (currentState?.command) {
                    currentState.command(item);
                  }
                });

                list.appendChild(itemElement);
              });

              component.appendChild(list);

              // Scroll to selected item
              if (selectedIndex >= 0) {
                requestAnimationFrame(() => {
                  const selectedElement = component?.querySelector(
                    `[data-index="${selectedIndex}"]`
                  );
                  if (selectedElement) {
                    (selectedElement as HTMLElement).scrollIntoView({
                      block: "nearest",
                    });
                  }
                });
              }
            };

            return {
              onStart: (props: any) => {
                // console.log("Suggestion onStart with props:", props);
                currentState = {
                  items: props.items || [],
                  selectedIndex: props.selectedIndex || 0,
                  query: props.query || "",
                  setSelectedIndex: props.setSelectedIndex || (() => {}),
                  command: props.command || (() => {}),
                  exit: props.exit || (() => {}),
                };

                component = document.createElement("div");
                component.className =
                  "bg-white border border-gray-200 rounded-lg shadow-lg w-72 text-sm overflow-auto max-h-44";
                component.style.position = "absolute";
                component.style.zIndex = "9999";
                // ⭐ CRITICAL FIX: Stop all mousedown/click events from leaving the dropdown container
                component.addEventListener("mousedown", (e) => {
                  e.stopPropagation();
                });
                component.addEventListener("click", (e) => {
                  e.stopPropagation();
                });
                document.body.appendChild(component);
                updateComponent();

                // Position the dropdown
                try {
                  const rect = props.clientRect?.();
                  if (rect) {
                    component.style.left = `${rect.left}px`;
                    component.style.top = `${rect.bottom + 6}px`;
                  }
                } catch (error) {
                  console.error("Error positioning dropdown:", error);
                }
              },

              onUpdate: (props: any) => {
                // console.log("Suggestion onUpdate with props:", props);
                if (currentState) {
                  currentState.items = props.items || [];
                  currentState.selectedIndex = props.selectedIndex || 0;
                  currentState.query = props.query || "";
                } else {
                  currentState = {
                    items: props.items || [],
                    selectedIndex: props.selectedIndex || 0,
                    query: props.query || "",
                    setSelectedIndex: props.setSelectedIndex || (() => {}),
                    command: props.command || (() => {}),
                    exit: props.exit || (() => {}),
                  };
                }
                updateComponent();

                // Reposition if needed
                try {
                  const rect = props.clientRect?.();
                  if (rect && component) {
                    component.style.left = `${rect.left}px`;
                    component.style.top = `${rect.bottom + 6}px`;
                  }
                } catch (error) {
                  console.error("Error repositioning dropdown:", error);
                }
              },

              onKeyDown: ({ event, ...props }: any) => {
                const key = event.key;

                if (!currentState) return false;

                const items = currentState.items;
                let selectedIndex = currentState.selectedIndex;
                const setSelectedIndex = currentState.setSelectedIndex;
                const command = currentState.command;
                const exit = currentState.exit;

                // console.log("Suggestion onKeyDown CALLED with:", {
                //   key: key,
                //   selectedIndex: selectedIndex,
                //   hasSetSelectedIndex: !!setSelectedIndex,
                // });

                if (items.length === 0) {
                  return false;
                }

                switch (key) {
                  case "ArrowDown":
                  case "ArrowUp": {
                    event.preventDefault();
                    event.stopPropagation();

                    const length = items.length;
                    let newIndex =
                      key === "ArrowDown"
                        ? (selectedIndex + 1) % length
                        : (selectedIndex - 1 + length) % length;

                    setSelectedIndex(newIndex); // Update Tiptap's internal index
                    currentState.selectedIndex = newIndex; // Update our local index
                    updateComponent(); // Force UI redraw

                    return true;
                  }

                  case "Enter":
                  case "Tab":
                    event.preventDefault();
                    event.stopPropagation();
                    if (command && items[selectedIndex]) {
                      command(items[selectedIndex]);
                    }
                    return true;

                  case "Escape":
                    event.preventDefault();
                    event.stopPropagation();
                    if (exit) {
                      exit();
                    }
                    return true;

                  default:
                    return false;
                }
              },

              onExit: () => {
                // console.log("Suggestion onExit");
                if (component?.parentNode) {
                  component.parentNode.removeChild(component);
                }
                component = null;
                currentState = null;
              },
            };
          },
        },
      }),
    ],

    content: initialHtml || "",

    editorProps: {
      handleKeyDown: (view, event: KeyboardEvent) => {
        // Handle backspace for mention deletion
        if (event.key === "Backspace") {
          const { state, dispatch } = view;
          const { selection } = state;

          if (
            selection.empty &&
            selection.$from.nodeBefore?.type?.name === "mention"
          ) {
            dispatch(
              state.tr.delete(selection.$from.pos - 1, selection.$from.pos)
            );
            return true;
          }
        }

        return false;
      },
      attributes: {
        class:
          "ProseMirror prose prose-sm focus:outline-none min-h-[80px] text-xs leading-5",
        spellCheck: "true",
        placeholder,
      },
    },

    onUpdate({ editor: e }) {
      const html = e.getHTML();
      const json = e.getJSON();
      const mentionIds = new Set<string>();
      const mentionsMap = new Map<string, string>();

      const walk = (node: any) => {
        if (!node) return;
        if (node.type === "mention" && node.attrs?.id) {
          mentionIds.add(node.attrs.id);
          mentionsMap.set(node.attrs.id, node.attrs.label || node.attrs.id);
        }
        if (node.content && Array.isArray(node.content)) {
          node.content.forEach(walk);
        }
      };
      walk(json);

      const mentionsMeta = Array.from(mentionsMap.entries()).map(
        ([id, label]) => ({
          id,
          label,
        })
      );

      onChange?.(Array.from(mentionIds), html, mentionsMeta);
    },
  });
  const searchQuery = useRef("");
  const memorizedOnChange = useCallback(
    (
      mentionIds: string[],
      commentHtml: string,
      mentionsMeta?: { id: string; label: string }[]
    ) => {
      onChange?.(mentionIds, commentHtml, mentionsMeta);
    },
    [onChange]
  );
  useEffect(() => {
    if (!editor) return;

    const focusEditor = () => {
      const editorElement = editor.view.dom as HTMLElement;
      if (editorElement && typeof editorElement.focus === "function") {
        // Use a short delay to ensure focus is applied *after* the modal's focus trap/autofocus logic runs.
        setTimeout(() => {
          editorElement.focus();
        }, 50); // 50ms is usually enough to beat the modal lifecycle
      }
    };

    // Defer the focus attempt to the next animation frame
    const animationFrameId = requestAnimationFrame(focusEditor);

    return () => {
      // Cleanup the animation frame and any potential timeout, though less critical here
      cancelAnimationFrame(animationFrameId);
    };
  }, [editor]);

  // expose editor instance to parent if requested
  useEffect(() => {
    if (!getEditor) return;
    getEditor(editor || null);
    return () => getEditor(null);
  }, [editor, getEditor]);

  // emit initial value once (if needed)
  useEffect(() => {
    if (!editor) return;
    const html = editor.getHTML();
    const json = editor.getJSON();
    const mentionIds = new Set<string>();
    const mentionsMap = new Map<string, string>();
    const walk = (node: any) => {
      if (!node) return;
      if (node.type === "mention" && node.attrs?.id) {
        mentionIds.add(node.attrs.id);
        mentionsMap.set(node.attrs.id, node.attrs.label || node.attrs.id);
      }
      node.content?.forEach?.(walk);
    };
    walk(json);
    const mentionsMeta = Array.from(mentionsMap.entries()).map(
      ([id, label]) => ({
        id,
        label,
      })
    );
    memorizedOnChange(Array.from(mentionIds), html, mentionsMeta);
  }, [editor, memorizedOnChange]);

  return (
    <div className="relative">
      <EditorContent editor={editor} />
      <style>{`
        .mention-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.125rem 0.75rem;
          border-radius: 9999px;
          background: #D0E7FF;
          border: 1px solid #B6DBFF;
          color: #0B5E9E;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}
